import type { DateRange } from '@/lib/date-presets'
import type { ExpenseBucket } from '@/lib/api/expense-report-api'

/**
 * Kỳ của báo cáo chi theo danh mục (M-83): xem theo Tháng / Quý / Năm, chọn một
 * kỳ cụ thể và một kỳ cùng loại để so sánh. Khoá kỳ nằm trên URL:
 * tháng `2026-09`, quý `2026-Q3`, năm `2026`. Ngày là chuỗi 'YYYY-MM-DD' theo
 * lịch VN; tính bằng Date UTC để không lệch múi giờ máy.
 */
export const PERIOD_UNITS = ['month', 'quarter', 'year'] as const
export type PeriodUnit = (typeof PERIOD_UNITS)[number]

/** Mốc biểu đồ khi xem theo Năm (xem Quý → tháng, xem Tháng → ngày). */
export const YEAR_STEPS = ['quarter', 'month'] as const
export type YearStep = (typeof YEAR_STEPS)[number]

export interface ParsedPeriod {
    unit: PeriodUnit
    year: number
    /** Tháng 1–12, quý 1–4; năm luôn 1. */
    index: number
}

const OPTION_COUNT: Record<PeriodUnit, number> = {
    month: 24,
    quarter: 12,
    year: 5,
}

const pad = (n: number) => String(n).padStart(2, '0')
const ymd = (d: Date) =>
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
const toDate = (s: string) => {
    const [y, m, d] = s.split('-').map(Number)
    return new Date(Date.UTC(y, m - 1, d))
}
const addDays = (s: string, n: number) => {
    const d = toDate(s)
    d.setUTCDate(d.getUTCDate() + n)
    return ymd(d)
}
const daysBetween = (from: string, to: string) =>
    Math.round((toDate(to).getTime() - toDate(from).getTime()) / 86_400_000)
const minYMD = (a: string, b: string) => (a < b ? a : b)

export function parsePeriodKey(key: string): ParsedPeriod | null {
    let m = /^(\d{4})-(\d{2})$/.exec(key)
    if (m) {
        const month = Number(m[2])
        return month >= 1 && month <= 12
            ? { unit: 'month', year: Number(m[1]), index: month }
            : null
    }
    m = /^(\d{4})-Q([1-4])$/.exec(key)
    if (m) return { unit: 'quarter', year: Number(m[1]), index: Number(m[2]) }
    m = /^(\d{4})$/.exec(key)
    return m ? { unit: 'year', year: Number(m[1]), index: 1 } : null
}

function formatKey(p: ParsedPeriod): string {
    if (p.unit === 'month') return `${p.year}-${pad(p.index)}`
    if (p.unit === 'quarter') return `${p.year}-Q${p.index}`
    return String(p.year)
}

/** Tháng đầu (0-based) và số tháng của kỳ. */
function monthSpan(p: ParsedPeriod): [number, number] {
    if (p.unit === 'month') return [p.index - 1, 1]
    if (p.unit === 'quarter') return [(p.index - 1) * 3, 3]
    return [0, 12]
}

/** Trọn kỳ lịch; khoá sai → ném lỗi (khoá đã qua validate ở URL). */
export function periodRange(key: string): DateRange {
    const p = parsePeriodKey(key)
    if (!p) throw new Error(`invalid period key: ${key}`)
    const [start, len] = monthSpan(p)
    return {
        from: ymd(new Date(Date.UTC(p.year, start, 1))),
        // Ngày 0 của tháng sau = ngày cuối kỳ.
        to: ymd(new Date(Date.UTC(p.year, start + len, 0))),
    }
}

export function currentPeriodKey(unit: PeriodUnit, today: string): string {
    const [y, m] = today.split('-').map(Number)
    const index =
        unit === 'month' ? m : unit === 'quarter' ? Math.ceil(m / 3) : 1
    return formatKey({ unit, year: y, index })
}

export function previousPeriodKey(key: string): string {
    const p = parsePeriodKey(key)
    if (!p) return key
    if (p.unit === 'year') return String(p.year - 1)
    const last = p.unit === 'month' ? 12 : 4
    return p.index > 1
        ? formatKey({ ...p, index: p.index - 1 })
        : formatKey({ ...p, year: p.year - 1, index: last })
}

/** Các kỳ chọn được: từ kỳ hiện tại lùi dần (không có kỳ tương lai). */
export function periodOptions(unit: PeriodUnit, today: string): string[] {
    const out = [currentPeriodKey(unit, today)]
    while (out.length < OPTION_COUNT[unit]) {
        out.push(previousPeriodKey(out[out.length - 1]))
    }
    return out
}

export interface ComparedRanges {
    view: DateRange
    compare: DateRange
    /** Kỳ xem chưa hết (chứa hôm nay). */
    running: boolean
}

/**
 * Khoảng ngày thật đem so. Kỳ xem đang chạy chỉ tới hôm nay, và kỳ so sánh
 * lấy cùng số ngày đầu kỳ (không vượt cuối kỳ) để hai bên công bằng. Không
 * khoảng nào vượt quá hôm nay.
 */
export function comparedRanges(
    viewKey: string,
    compareKey: string,
    today: string,
): ComparedRanges {
    const view = periodRange(viewKey)
    const compare = periodRange(compareKey)
    const running = view.from <= today && today <= view.to
    const viewTo = minYMD(view.to, today)
    const compareTo = running
        ? minYMD(
              compare.to,
              addDays(compare.from, daysBetween(view.from, viewTo)),
          )
        : compare.to
    return {
        view: { from: view.from, to: viewTo },
        compare: { from: compare.from, to: minYMD(compareTo, today) },
        running,
    }
}

export interface ChartBuckets {
    bucket: ExpenseBucket
    /** Khoảng gọi API; null = chưa có mốc nào trọn. */
    range: DateRange | null
    /** Ngày đầu từng mốc (khớp `period` API trả về). */
    points: string[]
}

/**
 * Mốc của biểu đồ đường: Năm → quý hoặc tháng (theo `step`), Quý → tháng,
 * Tháng → ngày. Chỉ lấy mốc đã trọn (kết thúc trước hôm nay) để đường không
 * tụt giả ở mốc đang chạy.
 */
export function chartBuckets(
    viewKey: string,
    step: YearStep,
    today: string,
): ChartBuckets {
    const unit = parsePeriodKey(viewKey)?.unit
    const bucket: ExpenseBucket =
        unit === 'year' ? step : unit === 'quarter' ? 'month' : 'day'
    const { from, to } = periodRange(viewKey)
    const points: string[] = []
    let lastEnd = ''
    let cur = from
    while (cur <= to) {
        const end = bucketEnd(cur, bucket)
        if (end >= today) break
        points.push(cur)
        lastEnd = end
        cur = addDays(end, 1)
    }
    return {
        bucket,
        range: points.length ? { from, to: lastEnd } : null,
        points,
    }
}

function bucketEnd(start: string, bucket: ExpenseBucket): string {
    if (bucket === 'day') return start
    const d = toDate(start)
    const months = bucket === 'month' ? 1 : 3
    return ymd(
        new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 0)),
    )
}
